import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { liveGameStyles as styles } from "./liveGameStyles.tailwind";
import LiveChatSection from "./Sections/LiveChat/LiveChatSection";
import PreviewGameCourt from "./Sections/PreviewGameCourt/PreviewGameCourt";
import Leaderboard from "./Sections/GameLeaderboard/Leaderboard";
import TeamDetails from "./Sections/TeamDetails/TeamDetails";
import { addToLiveGameActions, getGameDetails } from "../../../../redux/slices/scorerSlice";
import { connect, useSelector } from "react-redux";
import useSocket from "../../../../hooks/useSocket";
import SocketLoader from "../../../shared/Loaders/SocketLoader/SocketLoader";
import { SocketContext } from "../../../../context/socket";
import { LIVE_CHAT_CONSTANTS } from "../../../../lib/constants/websocketConstants";
import { getLeaderboardDetails } from "../../../../redux/slices/liveGameSlice";
import UserPoints from "./Sections/UserPoints/UserPoints";
import { isCurrentGameEnded } from "../../../GameScorer/Scorers/BasketballScorer/basketballScorer.data";
import { Navigate, useParams } from "react-router-dom";
import CommonLoader from "../../../shared/Loaders/CommonLoader/CommonLoader";
import GameScoreAndTimer from "./Sections/GameScoreAndTimer/GameScoreAndTimer";
import { LiveGameTimerProvider } from "../../../../context/GameTimer";
import routeConstant from "../../../../lib/constants/routeConstant";
import ExpandedLeaderboardModal from "../../../shared/Modals/ExpandedLeaderboard/ExpandedLeaderboard";

const LiveGameSection = ({ getGame, getLeaderboard, addCommentary }) => {
  const apiDataRef = useRef(false);
  const { gameId } = useParams();
  const { socket } = useContext(SocketContext);
  const { isConnected, socketError } = useSocket(socket);

  const { fetchingGame, fetchGameError, gameDetails } = useSelector(state => state.scorer);
  const { tokens } = useSelector((state) => state.deployToken);
  const { leaderboardLoading } = useSelector((state) => state.liveGame)
  const { currentSeason } = useSelector((state) => state.seasons);

  const [imageCache, setImageCache] = useState(null);
  const [zonesToBeHighlighted, setZonesToBeHighlighted] = useState([]); // to manage selected zone
  const [commentary, setCommentary] = useState(null); // to manage commentary
  const [tabValue, setTabValue] = useState(0); // State to track the selected tab
  const [showPoints, setShowPoints] = useState(false);
  const [popupPoint, setPopupPoint] = useState();
  const [replayedMessage, setReplayedMessage] = useState(null);
  const [isFullScreened, setIsFullScreened] = useState(false);
  const [openLeaderboardModal, setOpenLeaderboardModal] = useState(false);

  const handleNewCommentary = ({ commentary }) => {
    if(commentary === undefined) return;
  
    // const { player1Zone, player2Zone } = commentary;
    addCommentary({ commentary, reverse: true }); // reverse -> true

    if(isCurrentGameEnded(gameDetails)) return;
  
    const { player1Zone, player2Zone } = commentary;

    if(!replayedMessage) {
      // Check if zones are defined and filter out undefined values
      const validZones = [player1Zone, player2Zone]
      .filter((zone) => zone !== undefined) // Remove undefined values
      .map((zone) => zone?.replace('Z', '')); // Extract only the number part
      
      setZonesToBeHighlighted([]);
      setCommentary(null);

      setTimeout(() => {
        // Update the state with the new zones, replacing any previous zones
        setZonesToBeHighlighted([...validZones]);
        // Add the new commentary to the list
        setCommentary(commentary);
      }, 1000);
    }
  };

  const triggerPointsAnimation = (pointToBePopped) => {
    if(isCurrentGameEnded(gameDetails)) return;

    setShowPoints(true);
    setPopupPoint(pointToBePopped);

    // Reset after animation completes
    const tId = setTimeout(() => {
      setShowPoints(false);
    }, 700);

    return () => { clearTimeout(tId) };
  };

  const handleReplayCommentary = (message) => {
    setReplayedMessage(message);
    setZonesToBeHighlighted([]);

    const { player1Zone, player2Zone } = message;
    // Check if zones are defined and filter out undefined values
    const validZones = [player1Zone, player2Zone]
      .filter((zone) => zone !== undefined) // Remove undefined values
      .map((zone) => zone?.replace('Z', '')); // Extract only the number part

    // Update the state with the new zones, replacing any previous zones
    if (validZones?.length > 0) {
      setZonesToBeHighlighted([...validZones]);
    }
  }

  const handleOpenExpandedLeaderboard = () => {
    setOpenLeaderboardModal(true)
  }

  const callApi = useCallback(async () => {
    if (apiDataRef.current || !currentSeason?._id) return;
    apiDataRef.current = true;

    // data to be sent in purchased-tokens
    const data = {
      gameId: gameId,
    };

    getGame(data);
    getLeaderboard(data);
    // getFullLeaderboard({ ...data, limit: "50" });
  }, [gameId]);

  const preloadImages = async (urls) => {
    const cache = {};
  
    if (!Array.isArray(urls) || urls.some(url => typeof url !== 'string')) {
      console.error('🚨 preloadImages received invalid URLs:', urls);
      return cache;
    }
  
    await Promise.all(
      urls.map((url) =>
        new Promise((resolve) => {
          if (!url) {
            resolve(); // Skip null/undefined/bad values
            return;
          }
  
          const img = new Image();
          img.src = url;
  
          img.onload = () => {
            cache[url] = img;
            resolve();
          };
  
          img.onerror = (err) => {
            console.error(`Failed to load image: ${url}`, err);
            resolve(); // Fail silently, don't break the rest
          };
        })
      )
    );
  
    return cache;
  };
  
  useEffect(() => {
    const load = async () => {
      if(!gameDetails?.actionURLS) return;
      
      const cache = await preloadImages(gameDetails?.actionURLS);
      setImageCache(cache);
    };
    load();
  }, [gameDetails?.actionURLS]);
  
  useEffect(() => {
    callApi();
  }, [callApi]);

  useEffect(() => {
    // If there are zones to be highlighted, set a timeout to clear them
    if (zonesToBeHighlighted.length > 0) {
      let replayCommentaryTimeout = null;
      let onlyZoneHighlightTimeout = null;

      if(replayedMessage) {
        replayCommentaryTimeout = setTimeout(() => {
          setZonesToBeHighlighted([]); // Clear the highlighted zones
          setReplayedMessage(null);
          setCommentary(null);
        }, 6000)
      }else {
        // onlyZoneHighlightTimeout = setTimeout(() => {
        //   setZonesToBeHighlighted([]); // Clear the highlighted zones
        //   setCommentary(null);
        // }, 7500); // Clear after (7.5 s)  
      }

      // Cleanup function to clear the timeout if zones change or component unmounts
      return () => {
        clearTimeout(replayCommentaryTimeout)
        clearTimeout(onlyZoneHighlightTimeout)
      };
    }
  }, [zonesToBeHighlighted]);

  useEffect(() => {
    const handleResize = () => {
      try {
        if (window.innerWidth >= 500) {
          setIsFullScreened(false);
        }
      } catch (error) {
        console.log('Fullscreen exit error:', error);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [])
  
  useEffect(() => {
    if (!socket || !isConnected) return;
    console.log("✅PASSED: ", socket);
    // Send the gameId when the socket connects
    socket.emit(LIVE_CHAT_CONSTANTS.JOIN_ROOM, { gameId, scorer: true });
  }, [gameId, socket, isConnected]);

  // Loading
  if (leaderboardLoading || fetchingGame) {
    return <CommonLoader changeOrientation={false} />
  }

  // Verify if socket is connected
  if (!isConnected && !socketError) {
    return <SocketLoader changeOrientation={false} />
  }

  // Socket error
  if (socketError) {
    return <SocketLoader failed={true} changeOrientation={false} />
  }

  if(fetchGameError) {
    return <Navigate to={routeConstant.PAGE_NOT_FOUND} />
  }

  if (!imageCache) {
    return (
      <CommonLoader />
    );
  }

  return (
    <div className={`${styles.outer_container} ${isFullScreened ? styles.fullScreenContainer : ''}`}>
      <div className={`${styles.section} ${isFullScreened ? 'flex-1' : 'flex-[0.56]'}`}>
        {/* Team Details */}
        <TeamDetails />

        <div className="w-full hidden screen-500:flex flex-col gap-[10px] lg:hidden">
          <LiveGameTimerProvider>
            <GameScoreAndTimer socket={socket} />
          </LiveGameTimerProvider>  

          {/* User Points */}
          <UserPoints />
        </div>

        {/* Preview Game Court */}
        <PreviewGameCourt
          socket={socket}
          isFullScreened={isFullScreened}
          setIsFullScreened={setIsFullScreened}
          zonesToBeHighlighted={zonesToBeHighlighted}
          makeSelectable={false}
          isLiveGame={true}
          showPoints={showPoints}
          popupPoint={popupPoint}
          purchasedTokens={tokens}
          replayedMessage={replayedMessage}
          tabValue={tabValue}
          commentary={commentary}
          handleOpenExpandedLeaderboard={handleOpenExpandedLeaderboard}
        />

        <div className={`w-full ${isFullScreened ? 'hidden' : ''}`}>
          {/* Leaderboard */}
          <Leaderboard gameId={gameId} />
        </div>
      </div>

      <div className={`${styles.section} flex-[0.42] ${isFullScreened ? "hidden" : ""}`}>
        <div className="w-full hidden lg:flex flex-col gap-2">
          {/* Game Scores and Timer*/}
          <LiveGameTimerProvider>
            <GameScoreAndTimer socket={socket} />
          </LiveGameTimerProvider>

          {/* User Points */}
          <UserPoints />
        </div>

        {/* Live Interaction Section */}
        <LiveChatSection
          gameId={gameId}
          tabValue={tabValue}
          setTabValue={setTabValue}
          handleNewCommentary={handleNewCommentary}
          triggerPointsAnimation={triggerPointsAnimation}
          replayedMessage={replayedMessage}
          handleReplayCommentary={handleReplayCommentary}
        />
      </div>

      {openLeaderboardModal && (
        <ExpandedLeaderboardModal
          gameId={gameId}
          isOpen={openLeaderboardModal}
          isFullScreened={isFullScreened}
          onClose={() => setOpenLeaderboardModal(false)}
        />
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  getGame: (data) => dispatch(getGameDetails(data)),
  getLeaderboard: (data) => dispatch(getLeaderboardDetails(data)),
  addCommentary: (data) => dispatch(addToLiveGameActions(data)),
});

export default connect(null, mapDispatchToProps)(LiveGameSection);
